import React, { createContext, useContext, useEffect, useState } from "react";

import { RegisterAccounts } from "../services/api/JobOperation";
import { RegionConfiguration, Regions } from "../services/api/Regions";
import { UIConfiguration } from "../services/api/uiConfiguration";

export type Config = {
  regions: RegionConfiguration;
  notificationEnabled: boolean;
  proposition: string;
  primaryAccountName: string;
  secondaryAccountName: string;
  corsAllowedOrigin: string;
  singleTenantMode: boolean;
  registerAccounts: RegisterAccounts[];
};

type ConfigContextType = {
  config: Config;
};

const defaultConfig: Config = {
  regions: {
    primary_regions: [],
    secondary_regions: [],
  },
  notificationEnabled: false,
  proposition: "",
  primaryAccountName: "",
  secondaryAccountName: "",
  corsAllowedOrigin: "",
  singleTenantMode: true,
  registerAccounts: ["primary"],
};

const getConfigurations = async (): Promise<Config> => {
  const [uiConfig, regions] = await Promise.all([UIConfiguration.get(), Regions.getRegions()]);

  return {
    regions: regions,
    notificationEnabled: uiConfig.notification_enabled,
    proposition: uiConfig.atp_proposition,
    primaryAccountName: uiConfig.primary_account_name,
    secondaryAccountName: uiConfig.secondary_account_name,
    corsAllowedOrigin: uiConfig.cors_allowed_origin,
    singleTenantMode: uiConfig.secondary_account_name === "",
    registerAccounts: uiConfig.secondary_account_name === "" ? ["primary"] : ["primary", "secondary"],
  };
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) throw new Error("useConfig must be used within a <ConfigProvider>");
  return context;
};

export const ConfigProvider: React.FC<{ initialConfig?: Config; children: React.ReactNode }> = ({
  initialConfig,
  children,
}) => {
  const [config, setConfig] = useState<Config>(initialConfig || defaultConfig);

  useEffect(() => {
    if (!initialConfig) getConfigurations().then(setConfig);
  }, [initialConfig]);

  return <ConfigContext.Provider value={{ config }}> {children} </ConfigContext.Provider>;
};
